import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Zap, Target, Activity, Ticket, TrendingUp, Clock, Users, MessageSquare, BookOpen, ArrowRight, Shield } from 'lucide-react'

export default function DashboardPage() {
  const navigate = useNavigate()
  const [stats] = useState({
    tickets: 137,
    resolution: 87,
    responseTime: '4.2h',
    enrolled: 42,
    completed: 18,
  })

  const systems = [
    { name: '🎮 AI Control Center', status: 'operational', tickets: 23, color: 'from-blue-600 to-blue-500', path: '/ai-control-center' },
    { name: '📱 Social Media Manager', status: 'operational', tickets: 15, color: 'from-indigo-600 to-indigo-500', path: '/social-media-manager' },
    { name: '📊 Marketing AI Monitor', status: 'operational', tickets: 12, color: 'from-cyan-600 to-cyan-500', path: '/marketing-ai-monitor' },
    { name: '💰 Financial Monitor', status: 'operational', tickets: 8, color: 'from-teal-600 to-teal-500', path: '/financial-monitor' },
    { name: '☁️ Cloud Monitor', status: 'operational', tickets: 19, color: 'from-blue-700 to-blue-600', path: '/cloud-monitor' },
    { name: '📈 Company Growth', status: 'operational', tickets: 5, color: 'from-cyan-700 to-cyan-600', path: '/company-growth' },
    { name: '🔧 ITIL Help Desk', status: 'operational', tickets: 55, color: 'from-indigo-700 to-indigo-600', path: '/itil-help-desk' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Command Center Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-blue-700 to-cyan-600 p-8 text-white shadow-2xl border border-blue-500/20">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Shield className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Cybertection</h1>
                </div>
                <p className="text-blue-100 text-lg font-semibold">Central Command Center</p>
                <p className="text-blue-200 text-sm mt-1">AI-Powered Business Automation & Operations Hub</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">100%</div>
                <div className="text-sm text-blue-100">Systems Operational</div>
              </div>
            </div>

            {/* Status Bar */}
            <div className="mt-6 flex items-center space-x-8 pt-6 border-t border-blue-400/30">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/50"></div>
                <span className="text-sm font-semibold">All Systems Live</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-400 rounded-full shadow-lg shadow-green-400/50"></div>
                <span className="text-sm font-semibold">99.9% Uptime</span>
              </div>
              <div className="flex items-center space-x-2">
                <Activity className="w-4 h-4 text-yellow-300" />
                <span className="text-sm font-semibold">7 Active Systems</span>
              </div>
            </div>
          </div>
        </div>

        {/* Platform Overview */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center space-x-3">
            <Target className="w-7 h-7 text-blue-400" />
            <span>Platform Overview</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total Active Tickets', value: stats.tickets, icon: Ticket, color: 'from-blue-600 to-blue-500', textColor: 'text-blue-400' },
              { label: 'AI Resolution Rate', value: `${stats.resolution}%`, icon: TrendingUp, color: 'from-cyan-600 to-cyan-500', textColor: 'text-cyan-400' },
              { label: 'Response Time', value: stats.responseTime, icon: Clock, color: 'from-indigo-600 to-indigo-500', textColor: 'text-indigo-400' },
              { label: 'Training Enrolled', value: stats.enrolled, icon: Users, color: 'from-teal-600 to-teal-500', textColor: 'text-teal-400' },
            ].map((stat, idx) => {
              const Icon = stat.icon
              return (
                <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
                      <p className={`${stat.textColor} text-4xl font-black mt-3`}>{stat.value}</p>
                    </div>
                    <div className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-lg shadow-blue-500/20`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Active Systems */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center space-x-3">
            <Zap className="w-7 h-7 text-yellow-400" />
            <span>Active Command Systems</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {systems.map((system) => (
              <div
                key={system.name}
                className="group bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all cursor-pointer shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1"
              >
                <div className={`h-1.5 w-full bg-gradient-to-r ${system.color} rounded-full mb-5 shadow-lg`}></div>
                <h3 className="font-bold text-white text-sm mb-4">{system.name}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-300 text-xs font-semibold">Status</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/50"></div>
                      <span className="text-green-400 text-xs font-bold capitalize">{system.status}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-300 text-xs font-semibold">Active Tickets</span>
                    <span className="text-white text-xl font-black">{system.tickets}</span>
                  </div>
                </div>
                <button
                  onClick={() => navigate(system.path)}
                  className="w-full mt-4 py-2.5 px-3 bg-blue-600/20 hover:bg-blue-600/40 rounded-lg text-xs font-bold text-blue-300 flex items-center justify-between transition-all group-hover:text-blue-200 border border-blue-500/30 group-hover:border-blue-400/60 cursor-pointer">
                  <span>View System</span>
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions & Status */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-start-3 space-y-6">
            {/* Quick Actions */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
              <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
                <MessageSquare className="w-5 h-5 text-blue-400" />
                <span>Quick Actions</span>
              </h3>
              <div className="space-y-3">
                {[
                  { label: 'New Ticket', icon: MessageSquare, color: 'from-blue-600 to-blue-500', path: '/tickets', action: () => navigate('/tickets') },
                  { label: 'Add Knowledge Article', icon: BookOpen, color: 'from-cyan-600 to-cyan-500', path: '/knowledge-base', action: () => navigate('/knowledge-base') },
                  { label: 'Create Course', icon: Users, color: 'from-indigo-600 to-indigo-500', path: '/training', action: () => navigate('/training') },
                ].map((action, idx) => {
                  const Icon = action.icon
                  return (
                    <button
                      key={idx}
                      onClick={action.action}
                      className={`w-full px-4 py-3 bg-gradient-to-r ${action.color} rounded-lg text-sm font-bold transition-all flex items-center justify-between group hover:shadow-lg shadow-blue-500/20 text-white cursor-pointer`}>
                      <span className="flex items-center space-x-2">
                        <Icon size={16} />
                        <span>{action.label}</span>
                      </span>
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  )
                })}
              </div>
            </div>

            {/* System Health */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
              <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
                <Activity className="w-5 h-5 text-yellow-400" />
                <span>System Health</span>
              </h3>
              <div className="space-y-4">
                {[
                  { name: 'API Server', status: 'Online', color: 'text-green-400' },
                  { name: 'Database', status: 'Healthy', color: 'text-green-400' },
                  { name: 'AI Engine', status: 'Ready', color: 'text-green-400', pulse: true },
                ].map((system, idx) => (
                  <div key={idx} className="flex items-center justify-between pb-3 border-b border-blue-500/20 last:border-b-0">
                    <span className="text-blue-300 font-semibold text-sm">{system.name}</span>
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 bg-green-400 rounded-full shadow-lg shadow-green-400/50 ${system.pulse ? 'animate-pulse' : ''}`}></div>
                      <span className={`${system.color} text-sm font-bold`}>{system.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
